/**
 * ClassCard — unified class card component.
 *
 * Replaces the old multi-variant mega component with a single composable card
 * that accepts `actions` and `footer` slots for context-specific behavior.
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { ProgressiveImage } from "@/components/ui/progressive-image";
import { ClassMetadata } from "./class-metadata";
import { InstructorBadgeRow } from "./instructor-badge-row";
import type { ClassCardData } from "./class-card-data";
import { storage } from "@/lib/storage";
import {
  formatClassDateCompact,
  formatClassTimeRange,
  formatClassDuration,
} from "@/lib/utils/class-time-utils";
import {
  getBrowserTimezone,
  getTimezoneAbbreviation,
  timezonesHaveSameOffset,
} from "@/lib/utils/timezone-utils";

interface ClassCardProps {
  data: ClassCardData;
  /** Slot for top-right actions (e.g. dropdown menu) */
  actions?: ReactNode;
  /** Slot for content below metadata (e.g. register button) */
  footer?: ReactNode;
  /** Compact date/time mode. true = "Mon 3/14 • 7:00PM - 8:30PM", false = "7:00PM - 8:30PM • 1hr 30min". Omit for ClassMetadata's verbose format. */
  showDate?: boolean;
  /** Hide the date/time line entirely (e.g. studio calendar where time is shown as header) */
  hideDateTime?: boolean;
  /** Hide the status overlay badge (e.g. sold out, waitlist) */
  hideStatusBadge?: boolean;
  /** Show instructor badge below title. Default true. */
  showInstructorBadge?: boolean;
  /** Hide location/studio/address badges (e.g. when already on the studio's page) */
  hideLocation?: boolean;
  /** Hide the price badge */
  hidePrice?: boolean;
}

/** Extract HH:MM string from a Date for time formatting utilities */
function timeToHHMM(d: Date): string {
  return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
}

export function ClassCard({
  data,
  actions,
  footer,
  showDate,
  hideDateTime,
  hideStatusBadge,
  showInstructorBadge = true,
  hideLocation,
  hidePrice,
}: ClassCardProps) {
  const hasPhoto = !!data.photoUrl;

  // Build compact date/time string only when showDate is explicitly set.
  // When omitted, ClassMetadata uses its verbose format (e.g. "TUESDAY • MARCH 17 • 7:00 PM - 9:00 PM").
  let dateTimeString = hideDateTime ? undefined : data.customDateTimeString;
  if (
    !dateTimeString &&
    !hideDateTime &&
    showDate !== undefined &&
    data.startTime &&
    data.endTime &&
    !isNaN(data.startTime.getTime()) &&
    !isNaN(data.endTime.getTime())
  ) {
    const startHHMM = timeToHHMM(data.startTime);
    const endHHMM = timeToHHMM(data.endTime);
    const timeRange = formatClassTimeRange(startHHMM, endHHMM);

    // Check timezone difference
    const userTimezone = getBrowserTimezone();
    const isSameTimezone = data.timezone
      ? timezonesHaveSameOffset(data.timezone, userTimezone, data.startTime)
      : true;
    const tzSuffix =
      data.timezone && !isSameTimezone
        ? ` ${getTimezoneAbbreviation(data.timezone)}`
        : "";

    if (showDate) {
      const dateCompact = formatClassDateCompact(data.startTime);
      dateTimeString = `${dateCompact} \u2022 ${timeRange}${tzSuffix}`;
    } else {
      const duration = formatClassDuration(startHHMM, endHHMM);
      dateTimeString = `${timeRange}${tzSuffix} \u2022 ${duration}`;
    }
  }

  return (
    <Link to={data.href} className="block cursor-pointer">
      <Card className="group relative overflow-hidden p-2 cursor-pointer" data-testid="class-card">
        {/* Background: photo or gradient fallback */}
        {hasPhoto ? (
          <>
            <div className="absolute inset-0 pointer-events-none transition-transform duration-300 group-hover:scale-105">
              <ProgressiveImage
                src={data.photoUrl!}
                thumbnailSrc={storage.getThumbnailUrl(data.photoUrl!, {
                  width: 400,
                  height: 300,
                  quality: 30,
                })}
                alt={data.title}
                className="w-full h-full object-cover"
                style={
                  data.photoObjectPosition
                    ? { objectPosition: data.photoObjectPosition }
                    : undefined
                }
              />
            </div>
            <div className="absolute inset-0 bg-black/50 pointer-events-none" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent from-40% to-black/70 pointer-events-none" />
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-jeans/20 via-diamond/10 to-purple-500/20 pointer-events-none" />
        )}

        {/* Top-right: actions only (status badge moves inline with date/time row) */}
        {actions && (
          <div
            className="absolute z-20 top-3 right-3 flex items-center gap-1.5"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {actions}
          </div>
        )}

        {/* Content */}
        <div className="relative z-10 px-4 py-4 flex flex-col space-y-2">
          <ClassMetadata
            startTime={hideDateTime ? undefined : data.startTime}
            endTime={hideDateTime ? undefined : data.endTime}
            timezone={hideDateTime ? undefined : data.timezone}
            title={data.title}
            location={data.location}
            address={data.address}
            room={data.room}
            price={hidePrice ? null : data.price}
            customPriceString={hidePrice ? undefined : data.customPriceString}
            hidePrice={hidePrice}
            status={data.status}
            showTimezone={true}
            hasMenu={!!actions}
            variant={hasPhoto ? "light" : "default"}
            customDateTimeString={dateTimeString}
            studio={hideLocation ? null : data.studio}
            hideLocation={hideLocation}
            hideStatusBadge={hideStatusBadge}
            userRegistration={data.userRegistration ?? null}
            afterTitle={
              <InstructorBadgeRow
                instructor={data.instructor}
                collaborators={data.collaborators}
                isCollaborativeClass={data.isCollaborativeClass}
                ownerProfile={data.ownerProfile}
                variant={hasPhoto ? "light" : "default"}
                showInstructor={showInstructorBadge}
              />
            }
          />

          {/* Footer slot */}
          {footer && (
            <div
              className="pt-2"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {footer}
            </div>
          )}
        </div>
      </Card>
    </Link>
  );
}
